<template>
  <div v-if="isOpen" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white rounded-lg p-6 w-full max-w-md mx-4">
      <div class="flex justify-between items-center mb-4">
        <h2 class="text-xl font-semibold">{{ isLogin ? 'Iniciar Sesión' : 'Registrarse' }}</h2>
        <button @click="close" class="text-gray-500 hover:text-gray-700">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>

      <form @submit.prevent="handleSubmit" class="space-y-4">
        <div v-if="!isLogin">
          <label class="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
          <input
            v-model="form.name"
            type="text"
            required
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Tu nombre completo"
          />
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Email</label>
          <input
            v-model="form.email"
            type="email"
            required
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="tu@email.com"
          />
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Contraseña</label>
          <input
            v-model="form.password"
            type="password"
            required
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Mínimo 6 caracteres"
          />
        </div>

        <div v-if="error" class="text-red-600 text-sm bg-red-50 p-3 rounded-lg">
          {{ error }}
        </div>

        <button
          type="submit"
          :disabled="isLoading"
          class="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {{ isLoading ? 'Procesando...' : (isLogin ? 'Iniciar Sesión' : 'Registrarse') }}
        </button>
      </form>

      <div class="mt-4 text-center">
        <button
          @click="toggleMode"
          class="text-blue-600 hover:text-blue-700 text-sm"
        >
          {{ isLogin ? '¿No tienes cuenta? Regístrate' : '¿Ya tienes cuenta? Inicia sesión' }}
        </button>
      </div>

      <div class="mt-6">
        <div class="relative">
          <div class="absolute inset-0 flex items-center">
            <div class="w-full border-t border-gray-300"></div>
          </div>
          <div class="relative flex justify-center text-sm">
            <span class="px-2 bg-white text-gray-500">O continúa con</span>
          </div>
        </div>

        <div class="mt-4 flex gap-3">
          <button
            @click="loginWithGoogle"
            disabled
            class="flex-1 flex flex-col items-center justify-center gap-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-400 cursor-not-allowed opacity-50"
          >
            <div class="flex items-center gap-2">
              <svg class="w-4 h-4" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span class="text-sm">Google</span>
            </div>
            <span class="text-xs">(Próximamente)</span>
          </button>
          <button
            @click="loginWithMicrosoft"
            disabled
            class="flex-1 flex flex-col items-center justify-center gap-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-400 cursor-not-allowed opacity-50"
          >
            <div class="flex items-center gap-2">
              <svg class="w-4 h-4" viewBox="0 0 24 24">
                <path fill="#F25022" d="M1 1h10v10H1z"/>
                <path fill="#7FBA00" d="M13 1h10v10H13z"/>
                <path fill="#00A4EF" d="M1 13h10v10H1z"/>
                <path fill="#FFB900" d="M13 13h10v10H13z"/>
              </svg>
              <span class="text-sm">Microsoft</span>
            </div>
            <span class="text-xs">(Próximamente)</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive } from 'vue';
import { useAuthStore } from '../stores/auth';
import { useRouter } from 'vue-router';

interface Props {
  isOpen: boolean;
  initialMode?: 'login' | 'register';
}

const props = withDefaults(defineProps<Props>(), {
  initialMode: 'login'
});

const emit = defineEmits<{
  close: [];
  success: [];
}>();

const router = useRouter();
const authStore = useAuthStore();

const isLogin = ref(props.initialMode === 'login');
const isLoading = ref(false);
const error = ref<string | null>(null);

const form = reactive({
  name: '',
  email: '',
  password: ''
});

const toggleMode = () => {
  isLogin.value = !isLogin.value;
  error.value = null;
  form.name = '';
  form.email = '';
  form.password = '';
};

const handleSubmit = async () => {
  isLoading.value = true;
  error.value = null;

  try {
    console.log('🔐 Intentando:', isLogin.value ? 'login' : 'register');
    console.log('📧 Email:', form.email);
    console.log('👤 Nombre:', form.name);
    
    if (isLogin.value) {
      console.log('🔑 Ejecutando login...');
      await authStore.login(form.email, form.password);
    } else {
      console.log('📝 Ejecutando registro...');
      await authStore.register(form.email, form.password, form.name);
    }
    
    console.log('✅ Autenticación exitosa');
    emit('success');
    close();
    router.push('/reports');
  } catch (err: any) {
    console.error('❌ Error en autenticación:', err);
    error.value = err.message;
  } finally {
    isLoading.value = false;
  }
};

const loginWithGoogle = () => {
  const base = (import.meta as any).env?.VITE_API_URL || (import.meta as any).env?.VITE_API_BASE_URL || 'http://localhost:8010';
  window.location.href = `${base}/api/auth/google`;
};

const loginWithMicrosoft = () => {
  const base = (import.meta as any).env?.VITE_API_URL || (import.meta as any).env?.VITE_API_BASE_URL || 'http://localhost:8010';
  window.location.href = `${base}/api/auth/microsoft`;
};

const close = () => {
  emit('close');
  error.value = null;
  form.name = '';
  form.email = '';
  form.password = '';
};
</script>
